export interface WebSocketMessage {
  type: string;
  data: any;
}

export type WebSocketConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

class WebSocketManager {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private messageHandlers = new Map<string, (data: any) => void>();
  private statusHandlers = new Set<(status: WebSocketConnectionStatus) => void>();
  private reconnectTimer: NodeJS.Timeout | null = null;
  private connectionStatus: WebSocketConnectionStatus = 'disconnected';
  private idToken: string | null = null;
  private connectionAttemptTimeout: NodeJS.Timeout | null = null;

  constructor() {
    console.log('[WebSocket] Manager initialized');
  }

  /**
   * Connect to WebSocket API with JWT ID token
   */
  async connect(idToken: string): Promise<void> {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('[WebSocket] Already connected');
      return;
    }

    this.idToken = idToken;
    this.setStatus('connecting');

    try {
      // Get WebSocket URL from environment
      const wsUrl = this.getWebSocketUrl();
      const urlWithToken = `${wsUrl}?token=${encodeURIComponent(idToken)}`;

      console.log('[WebSocket] Connecting to:', wsUrl.replace(/\?.*/, ''));

      this.ws = new WebSocket(urlWithToken);

      // Set connection timeout
      this.connectionAttemptTimeout = setTimeout(() => {
        if (this.ws?.readyState === WebSocket.CONNECTING) {
          console.log('[WebSocket] Connection timeout, closing...');
          this.ws.close();
        }
      }, 10000); // 10 second timeout

      this.ws.onopen = () => {
        console.log('[WebSocket] Connected successfully');
        this.reconnectAttempts = 0;
        this.reconnectDelay = 1000;

        if (this.connectionAttemptTimeout) {
          clearTimeout(this.connectionAttemptTimeout);
          this.connectionAttemptTimeout = null;
        }

        this.setStatus('connected');
      };

      this.ws.onmessage = (event) => {
        console.log('[WebSocket] Message received:', event.data);
        this.handleMessage(event);
      };

      this.ws.onclose = (event) => {
        console.log('[WebSocket] Connection closed:', event.code, event.reason);
        this.cleanup();
        this.setStatus('disconnected');
        
        // Attempt reconnection unless it was a clean close
        if (event.code !== 1000) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        console.error('[WebSocket] Connection error:', error);
        this.cleanup();
        this.setStatus('error');
      };

    } catch (error) {
      console.error('[WebSocket] Failed to connect:', error);
      this.setStatus('error');
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect(): void {
    console.log('[WebSocket] Disconnecting...');
    this.cleanup();

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.setStatus('disconnected');
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Get current connection status
   */
  getStatus(): WebSocketConnectionStatus {
    return this.connectionStatus;
  }

  /**
   * Subscribe to message type
   */
  onMessage(type: string, handler: (data: any) => void): () => void {
    this.messageHandlers.set(type, handler);
    
    // Return unsubscribe function
    return () => {
      this.messageHandlers.delete(type);
    };
  }

  /**
   * Subscribe to status changes
   */
  onStatusChange(handler: (status: WebSocketConnectionStatus) => void): () => void {
    this.statusHandlers.add(handler);
    
    // Return unsubscribe function
    return () => {
      this.statusHandlers.delete(handler);
    };
  }

  /**
   * Send message (if needed for future features)
   */
  sendMessage(message: WebSocketMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('[WebSocket] Cannot send message - not connected');
    }
  }

  private getWebSocketUrl(): string {
    // Try to get from config.json first
    const config = (window as any)._config;
    if (config?.WS_URL) {
      console.log('[WebSocket] Using WS_URL from config:', config.WS_URL);
      return config.WS_URL;
    }

    // Fallback to environment variable
    const envUrl = (import.meta as any).env?.VITE_WS_URL;
    if (envUrl) {
      console.log('[WebSocket] Using VITE_WS_URL from env:', envUrl);
      return envUrl;
    }

    console.error('[WebSocket] No WebSocket URL configured. Available config:', config);
    throw new Error('WebSocket URL not configured. Please set WS_URL in config.json or VITE_WS_URL environment variable.');
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const message: WebSocketMessage = JSON.parse(event.data);

      // Handle built-in message types
      if (message.type === 'connection.established') {
        console.log('[WebSocket] Connection establishment confirmed');
        return;
      }

      const handler = this.messageHandlers.get(message.type);

      if (handler) {
        handler(message.data);
      } else {
        console.warn('[WebSocket] No handler for message type:', message.type);
      }
    } catch (error) {
      console.error('[WebSocket] Failed to parse message:', error);
    }
  }



  private cleanup(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.connectionAttemptTimeout) {
      clearTimeout(this.connectionAttemptTimeout);
      this.connectionAttemptTimeout = null;
    }
  }

  private setStatus(status: WebSocketConnectionStatus): void {
    if (this.connectionStatus !== status) {
      this.connectionStatus = status;
      console.log('[WebSocket] Status changed to:', status);
      
      // Notify all status handlers
      this.statusHandlers.forEach(handler => {
        try {
          handler(status);
        } catch (error) {
          console.error('[WebSocket] Status handler error:', error);
        }
      });
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[WebSocket] Max reconnection attempts reached');
      this.setStatus('error');
      return;
    }

    if (!this.idToken) {
      console.error('[WebSocket] No token available for reconnection');
      this.setStatus('error');
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    console.log(`[WebSocket] Scheduling reconnect in ${this.reconnectDelay}ms (attempt ${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})`);
    
    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000); // Exponential backoff, max 30s
      if (this.idToken) {
        this.connect(this.idToken);
      }
    }, this.reconnectDelay);
  }
}

// Export singleton instance
export const webSocketManager = new WebSocketManager();
